/**
 * demoShowcaseHistorial.data.ts — Ventas/movimientos/creditos demo para historial y movimientos.
 *
 * Escenario Juan Pérez · Caja principal: devolución parcial, anulación total,
 * credito parcial + abono, anulación parcial de ítems. Se fusiona al cierre pc1 mañana.
 */

import type {
  AnulacionVenta,
  Devolucion,
  Credito,
  MovimientoCaja,
  Venta,
} from "../types/caja.types";
import { MAY12_PUNTO_ID, MAY12_SESION_MAIN_ID } from "./may12CajaDemoSeed.data";
import { resolveDemoCatalogLine } from "./demoInventoryProductMap";

const JP = { id: "emp_1", nombre: "Juan Pérez" };
const PUNTO = "Caja principal";

const line = (
  pid: string,
  nombre: string,
  cant: number,
  pUnit: number,
): Venta["productos"][0] => {
  const resolved = resolveDemoCatalogLine(pid, nombre, MAY12_PUNTO_ID);
  return {
    productoId: resolved.productoId,
    nombre: resolved.nombre,
    cantidad: cant,
    precioUnitario: pUnit,
  };
};

export const DEMO_SHOWCASE_DEV_ID = "DEV-1781968797860";
export const DEMO_SHOWCASE_VENTA_DEV = "demo-showcase-v-dev";
export const DEMO_SHOWCASE_VENTA_CREDITO = "demo-showcase-v-credito";
export const DEMO_SHOWCASE_CREDITO_JUAN = "demo-showcase-credito-juan";

const devolucionShowcase: Devolucion = {
  id: DEMO_SHOWCASE_DEV_ID,
  ventaId: DEMO_SHOWCASE_VENTA_DEV,
  fecha: "2026-05-12T10:19:00.000-05:00",
  items: [line("des-caldo-pescado", "Caldo de Pescado", 1, 8_000)],
  montoReembolsado: 8_000,
  metodoPago: "efectivo",
  motivoDevolucion: "dañado",
  usuarioId: JP.id,
  userName: JP.nombre,
};

export function buildDemoShowcaseVentas(): Venta[] {
  const ventaDev: Venta = {
    id: DEMO_SHOWCASE_VENTA_DEV,
    fecha: "2026-05-12T10:19:39.000-05:00",
    total: 27_000,
    metodoPago: "efectivo",
    productos: [
      line("des-caldo-pescado", "Caldo de Pescado", 1, 8_000),
      line("des-caldo-pata", "Caldo de Pata", 1, 9_000),
      line("des-arroz-leche", "Arroz con Leche", 1, 4_000),
      line("des-proteina", "Proteína Adicional", 1, 6_000),
    ],
    usuarioId: JP.id,
    userName: JP.nombre,
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    puntoCajaNombre: PUNTO,
    estado: "devolucion-parcial",
    devoluciones: [devolucionShowcase],
  };

  const ventaAnulada: Venta = {
    id: "T-23567448",
    fecha: "2026-05-12T10:20:00.000-05:00",
    total: 22_500,
    metodoPago: "efectivo",
    productos: [line("des-item-credito", "Producto crédito demo", 1, 22_500)],
    usuarioId: JP.id,
    userName: JP.nombre,
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    puntoCajaNombre: PUNTO,
    estado: "anulada",
    anuladaEn: "2026-05-12T10:21:00.000-05:00",
    anuladaPor: JP.nombre,
    motivoAnulacion: "me equivoque",
    clienteCreditoNombre: "juan",
  };

  const ventaCredito: Venta = {
    id: DEMO_SHOWCASE_VENTA_CREDITO,
    fecha: "2026-05-12T10:21:30.000-05:00",
    total: 22_500,
    metodoPago: "credito",
    productos: [
      line("des-item-credito-2", "Producto crédito parcial", 1, 22_500),
    ],
    usuarioId: JP.id,
    userName: JP.nombre,
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    puntoCajaNombre: PUNTO,
    estado: "activa",
    creditoId: DEMO_SHOWCASE_CREDITO_JUAN,
    clienteCreditoId: "cli-demo-juan",
    clienteCreditoNombre: "juan",
  };

  const anulacionParcial: AnulacionVenta = {
    id: "anu-showcase-parcial-1",
    ventaId: "T-51293299",
    fecha: "2026-05-12T10:23:12.000-05:00",
    modo: "parcial",
    items: [line("des-monona", "Moñona", 1, 14_000)],
    montoAnulado: 14_000,
    motivoAnulacion: "producto error",
    usuarioId: JP.id,
    userName: JP.nombre,
  };

  const ventaAnuParcial: Venta = {
    id: "T-51293299",
    fecha: "2026-05-12T10:23:12.000-05:00",
    total: 16_500,
    metodoPago: "efectivo",
    productos: [
      line("des-huevos", "Huevos al Gusto", 1, 8_000),
      line("des-arepa-huevo", "Arepa con Huevo y Queso", 1, 8_500),
      line("des-monona", "Moñona", 1, 14_000),
    ],
    usuarioId: JP.id,
    userName: JP.nombre,
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    puntoCajaNombre: PUNTO,
    estado: "activa",
    anulaciones: [anulacionParcial],
  };

  return [ventaAnuParcial, ventaCredito, ventaAnulada, ventaDev];
}

export function buildDemoShowcaseMovimientos(): MovimientoCaja[] {
  return [
    {
      id: "demo-showcase-m-anu-parcial",
      tipo: "anulacion",
      monto: 14_000,
      motivo: "Anulación parcial de venta T-51293299: producto error",
      fecha: "2026-05-12T10:23:12.000-05:00",
      usuarioId: JP.id,
      userName: JP.nombre,
      metodoPago: "efectivo",
      sesionId: MAY12_SESION_MAIN_ID,
      puntoCajaId: MAY12_PUNTO_ID,
      puntoCajaNombre: PUNTO,
      ventaId: "T-51293299",
      estado: "activo",
    },
    {
      id: "demo-showcase-m-abono",
      tipo: "ingreso",
      monto: 11_250,
      motivo: "Abono a crédito: juan",
      fecha: "2026-05-12T10:22:00.000-05:00",
      usuarioId: JP.id,
      userName: JP.nombre,
      metodoPago: "efectivo",
      sesionId: MAY12_SESION_MAIN_ID,
      puntoCajaId: MAY12_PUNTO_ID,
      puntoCajaNombre: PUNTO,
      creditoAbono: true,
      creditoId: DEMO_SHOWCASE_CREDITO_JUAN,
      estado: "activo",
    },
    {
      id: "demo-showcase-m-anu-total",
      tipo: "anulacion",
      monto: 22_500,
      motivo: "Anulación de venta T-23567448: me equivoque",
      fecha: "2026-05-12T10:21:00.000-05:00",
      usuarioId: JP.id,
      userName: JP.nombre,
      metodoPago: "efectivo",
      sesionId: MAY12_SESION_MAIN_ID,
      puntoCajaId: MAY12_PUNTO_ID,
      puntoCajaNombre: PUNTO,
      ventaId: "T-23567448",
      estado: "activo",
    },
    {
      id: "demo-showcase-m-dev",
      tipo: "devolucion",
      monto: 8_000,
      motivo: `Devolución #${DEMO_SHOWCASE_DEV_ID}: dañado`,
      fecha: "2026-05-12T10:19:00.000-05:00",
      usuarioId: JP.id,
      userName: JP.nombre,
      metodoPago: "efectivo",
      sesionId: MAY12_SESION_MAIN_ID,
      puntoCajaId: MAY12_PUNTO_ID,
      puntoCajaNombre: PUNTO,
      ventaId: DEMO_SHOWCASE_VENTA_DEV,
      estado: "activo",
    },
  ];
}

export function buildDemoShowcaseCreditos(): Credito[] {
  return [
    {
      id: DEMO_SHOWCASE_CREDITO_JUAN,
      clienteId: "cli-demo-juan",
      clienteNombre: "juan",
      total: 22_500,
      abonado: 11_250,
      saldoPendiente: 11_250,
      fecha: "2026-05-12T10:21:30.000-05:00",
      usuarioId: JP.id,
      userName: JP.nombre,
      sesionId: MAY12_SESION_MAIN_ID,
      productos: [
        line("des-item-credito-2", "Producto crédito parcial", 1, 22_500),
      ],
      abonos: [
        {
          id: "demo-showcase-abono-1",
          monto: 11_250,
          fecha: "2026-05-12T10:22:00.000-05:00",
          metodoPago: "efectivo",
          usuarioId: JP.id,
          userName: JP.nombre,
        },
      ],
      estado: "parcial",
    },
  ];
}

export function isDemoShowcaseVentaId(id: string): boolean {
  return (
    id.startsWith("demo-showcase-") ||
    id === "T-23567448" ||
    id === "T-51293299"
  );
}

export function isDemoShowcaseMovimientoId(id: string): boolean {
  return id.startsWith("demo-showcase-m-");
}

export function isDemoShowcaseCreditoId(id: string): boolean {
  return id.startsWith("demo-showcase-credito-");
}
